/**
 * AnalyticsSection — the Book screen's collapsible analytics block
 * (chat-centric revamp §4): the theme exposure chart, the four allocation
 * pies and the Risk / Calibration links that used to top the dashboard.
 *
 * Collapsed by default: the KPI strip and the positions table are the Book's
 * first answer, the allocation deep-dive is opt-in. Expanding renders the
 * charts in place — they keep their own data hooks, so a collapsed section
 * costs nothing.
 */

import React, { useMemo, useState } from 'react';
import { Text, TouchableOpacity, View, StyleSheet } from 'react-native';
import { PieChart } from 'react-native-chart-kit';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { RiskWidget } from '../risk/RiskWidget';
import { ThemeExposureChart } from '../dashboard/ThemeExposureChart';
import type { DashboardData } from '../../services/dashboardApi';

interface AnalyticsSectionProps {
  data: DashboardData | null;
  onNavigateRisk: () => void;
  onNavigateCalibration: () => void;
}

interface PieRow {
  name: string;
  population: number;
  percentage: number | null;
  color: string;
  legendFontColor: string;
  legendFontSize: number;
}

const CHART_HEIGHT = 200;
const CHART_WIDTH = 200;
const CHART_RADIUS = 65;
const CHART_PADDING_LEFT = 35;

export function AnalyticsSection({
  data,
  onNavigateRisk,
  onNavigateCalibration,
}: AnalyticsSectionProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const [expanded, setExpanded] = useState(false);

  const generateColors = (count: number): string[] => theme.colors.chartColors.slice(0, count);

  // Same chart skin DashboardScreen uses, so the pies read identically in
  // both places until the T9 sweep retires the dashboard.
  const chartConfig = {
    backgroundGradientFrom: 'transparent',
    backgroundGradientTo: 'transparent',
    color: (opacity = 1) => {
      const rgb = theme.id === 'bauhaus' ? '120, 120, 120' : '250, 250, 250';
      return `rgba(${rgb}, ${opacity})`;
    },
    labelColor: (opacity = 1) => {
      const rgb = theme.id === 'bauhaus' ? '80, 80, 80' : '250, 250, 250';
      return `rgba(${rgb}, ${opacity})`;
    },
  };

  const buildRows = (
    breakdown: { key: string; value: number; percentage: number }[] | undefined,
    nameFor: (key: string) => string
  ): PieRow[] | undefined =>
    breakdown?.map((item, index) => ({
      name: nameFor(item.key),
      population: item.value,
      percentage: item.percentage,
      color: generateColors(breakdown.length)[index],
      legendFontColor: theme.colors.text,
      legendFontSize: 12,
    }));

  const pies = [
    {
      title: t('dashboard.strategyBreakdown', { defaultValue: 'Conviction Strategy' }),
      rows: buildRows(
        data?.strategy_breakdown?.map((s) => ({
          key: s.strategy,
          value: s.value,
          percentage: s.percentage,
        })),
        (key) => t(`dashboard.strategy${key}`, { defaultValue: key })
      ),
      empty: !data?.strategy_breakdown?.length,
    },
    {
      title: t('dashboard.sectorBreakdown'),
      rows: buildRows(
        data?.sector_breakdown?.map((s) => ({
          key: s.sector,
          value: s.value,
          percentage: s.percentage,
        })),
        (key) => t(`sectors.${key}`, { defaultValue: key })
      ),
      empty: !data?.sector_breakdown?.length,
    },
    {
      title: t('dashboard.assetBreakdown', { defaultValue: 'Asset Breakdown' }),
      rows: buildRows(
        data?.asset_category_breakdown?.map((s) => ({
          key: s.asset_category,
          value: s.value,
          percentage: s.percentage,
        })),
        (key) => t(`assetCategories.${key}`, { defaultValue: key })
      ),
      empty: !data?.asset_category_breakdown?.length,
    },
    {
      title: t('dashboard.regionBreakdown'),
      rows: buildRows(
        data?.geographic_breakdown?.map((s) => ({
          key: s.country,
          value: s.value,
          percentage: s.percentage,
        })),
        (key) => t(`countries.${key}`, { defaultValue: key })
      ),
      empty: !data?.geographic_breakdown?.length,
    },
  ];

  return (
    <View style={styles.section}>
      <TouchableOpacity
        style={styles.header}
        onPress={() => setExpanded((prev) => !prev)}
        accessibilityRole="button"
        testID="analytics-toggle"
      >
        <Text style={styles.headerTitle}>{t('book.analytics')}</Text>
        <Text style={styles.chevron}>{expanded ? '⌃' : '⌄'}</Text>
      </TouchableOpacity>

      {expanded && (
        <View style={styles.content} testID="analytics-content">
          <ThemeExposureChart />
          <RiskWidget onPress={onNavigateRisk} />
          <TouchableOpacity
            style={styles.linkRow}
            onPress={onNavigateCalibration}
            accessibilityRole="button"
            testID="analytics-calibration-link"
          >
            <Text style={styles.linkRowTitle}>{t('calibration.title')}</Text>
            <Text style={styles.linkRowArrow}>›</Text>
          </TouchableOpacity>
          {pies.map((pie) => (
            <View key={pie.title} style={styles.chartCard}>
              <Text style={styles.chartCardTitle}>{pie.title}</Text>
              {!pie.empty && pie.rows && pie.rows.length > 0 ? (
                <View style={styles.chartSection}>
                  <View style={styles.chartContainer}>
                    <PieChart
                      data={pie.rows}
                      width={CHART_WIDTH}
                      height={CHART_HEIGHT}
                      chartConfig={chartConfig}
                      accessor="population"
                      backgroundColor="#00000000"
                      hasLegend={false}
                      // chart-kit's d.ts mis-types paddingLeft as a string and
                      // omits donut/radius, though the runtime supports all
                      // three — DashboardScreen passes the same trio.
                      {...({
                        paddingLeft: CHART_PADDING_LEFT,
                        donut: true,
                        radius: CHART_RADIUS,
                      } as any)}
                    />
                  </View>
                  <View style={styles.legendWrap}>
                    {pie.rows.map((row) => (
                      <View key={row.name} style={styles.legendChip}>
                        <View style={[styles.legendDot, { backgroundColor: row.color }]} />
                        <Text
                          style={[styles.legendText, { color: theme.colors.text }]}
                          numberOfLines={1}
                        >
                          {row.name}
                        </Text>
                        {row.percentage !== null && (
                          <Text
                            style={[styles.legendPercent, { color: theme.colors.textSecondary }]}
                          >
                            {row.percentage.toFixed(1)}%
                          </Text>
                        )}
                      </View>
                    ))}
                  </View>
                </View>
              ) : (
                <Text style={styles.emptyText}>
                  {t('dashboard.noBreakdownAvailable', { defaultValue: 'No breakdown available' })}
                </Text>
              )}
            </View>
          ))}
        </View>
      )}
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    section: {
      marginHorizontal: theme.spacing.lg,
      marginTop: theme.spacing.md,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: theme.colors.surface,
      borderRadius: theme.radii.lg,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderColor: theme.colors.border,
      padding: theme.spacing.lg,
    },
    headerTitle: {
      color: theme.colors.text,
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    chevron: {
      color: theme.colors.textSecondary,
      fontSize: theme.typography.fontSizeLg,
    },
    content: { gap: theme.spacing.sm, marginTop: theme.spacing.sm },
    linkRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: theme.colors.surface,
      borderRadius: theme.radii.lg,
      borderWidth: 1,
      borderColor: theme.colors.border,
      padding: theme.spacing.lg,
    },
    linkRowTitle: {
      color: theme.colors.text,
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    linkRowArrow: {
      color: theme.colors.textSecondary,
      fontSize: theme.typography.fontSizeLg,
    },
    chartCard: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.radii.lg,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderColor: theme.colors.border,
      padding: theme.spacing.lg,
    },
    chartCardTitle: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightBold,
      color: theme.colors.text,
    },
    chartSection: {
      alignItems: 'center',
      paddingTop: theme.spacing.xs,
    },
    chartContainer: {
      alignItems: 'center',
      justifyContent: 'center',
    },
    legendWrap: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 6,
      marginTop: theme.spacing.xs,
    },
    legendChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    legendDot: {
      width: 8,
      height: 8,
      borderRadius: 4,
    },
    legendText: {
      fontSize: theme.typography.fontSizeXs,
    },
    legendPercent: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    emptyText: {
      fontSize: theme.typography.fontSizeMd,
      color: theme.colors.textSecondary,
      textAlign: 'center',
      paddingVertical: theme.spacing.xl,
    },
  });
}
